import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StatusBadge from '../common/StatusBadge';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import IdentityCheck from './IdentityCheck';
import IncomeCheck from './IncomeCheck';
import AffordabilityCalc from './AffordabilityCalc';
import CreditCheck from './CreditCheck';
import ManualReview from './ManualReview';
import verificationService from '../../services/verificationService';
import documentService from '../../services/documentService';
import { CHECK_TYPES } from '../../utils/constants';
import './VerificationTask.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const VerificationTask = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [showManualReview, setShowManualReview] = useState(false);
  const [task, setTask] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const data = await verificationService.getTask(taskId);
      setTask(data);
      if (data.application_request_id) {
        try {
          setDocuments(await documentService.getDocuments(data.application_request_id));
        } catch (err) {
          // Non-fatal: task context still renders without the evidence list.
        }
      }
    } catch (err) {
      setLoadError(messageFrom(err, 'This verification task could not be loaded.'));
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const downloadDocument = async (docId, filename) => {
    try {
      const blob = await documentService.getDocument(docId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This document could not be opened.') });
    }
  };

  const completeReview = async (vals) => {
    await verificationService.updateTask(taskId, {
      status: vals.status,
      result_summary: vals.evidenceRef ? `${vals.notes}\n\nEvidence: ${vals.evidenceRef}` : vals.notes,
      reviewer_notes: vals.notes,
    });
    setShowManualReview(false);
    await load();
  };

  const renderCheckComponent = () => {
    switch (task.check_type) {
      case CHECK_TYPES.IDENTITY: return <IdentityCheck task={task} />;
      case CHECK_TYPES.INCOME: return <IncomeCheck task={task} />;
      case CHECK_TYPES.AFFORDABILITY: return <AffordabilityCalc task={task} />;
      case CHECK_TYPES.CREDIT: return <CreditCheck task={task} />;
      default: return <div>Check type component pending...</div>;
    }
  };

  if (loadError) return <Alert type="error" message={loadError} />;
  if (!task) return <LoadingSpinner fullPage />;

  return (
    <div className="verification-task">
      <div className="task-header card">
        <div className="card-body">
          <div className="task-header__top">
            <div className="task-title-group">
              <button className="btn-back" onClick={() => navigate('/verification')}>&larr; Back</button>
              <h1 className="task-title">{task.check_type.replace('_', ' ').toUpperCase()} CHECK</h1>
              <StatusBadge status={task.status} />
            </div>
            <div className="task-assignee">
              Assigned to: <strong>{task.assigned_to || 'Unassigned'}</strong>
            </div>
          </div>

          <div className="task-context">
            <div className="context-item">
              <span className="label">Applicant:</span> {task.applicant_name || 'Applicant'}
            </div>
            <div className="context-item">
              <span className="label">Property:</span> {task.property_address || '—'}
            </div>
            <div className="context-item">
              <span className="label">Valid Consent:</span>
              {task.consent_valid ? <span className="success-text">Confirmed</span> : <span className="danger-text">Missing</span>}
            </div>
          </div>
        </div>
      </div>

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {!task.consent_valid && (
        <Alert type="error" message="Valid consent is missing for this check. Do not proceed until consent is verified." />
      )}

      {showManualReview ? (
        <ManualReview task={task} onCancel={() => setShowManualReview(false)} onComplete={completeReview} />
      ) : (
        <div className="task-content">
          <div className="check-workspace">
            {renderCheckComponent()}
          </div>

          <div className="task-sidebar">
            <div className="card">
              <div className="card-header"><h3>Actions</h3></div>
              <div className="card-body task-actions">
                <button className="btn-primary" onClick={() => setShowManualReview(true)}>
                  Manual Review & Decision
                </button>
                <p className="helper-text" style={{marginTop: '16px'}}>
                  Automated adverse decisions are disabled. Please use manual review to log negative results or flag for attention.
                </p>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Evidence</h3></div>
              <div className="card-body">
                {documents.length > 0 ? (
                  <ul className="evidence-list">
                    {documents.map(doc => (
                      <li key={doc.id}>
                        <a href="#" onClick={(e) => { e.preventDefault(); downloadDocument(doc.id, doc.original_filename); }}>
                          {doc.original_filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="helper-text">No documents uploaded for this application yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationTask;
